import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { prefetchLyrics } from '../services/LyricsPrefetcher';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MusicKitContext = createContext(null);

// Suppress MusicKit errors from showing browser alerts/error overlays
// This prevents various MusicKit SDK internal errors from showing to users
if (typeof window !== 'undefined') {
  // List of MusicKit error messages to suppress
  const SUPPRESSED_ERRORS = [
    'without a previous descriptor',
    'without a previous stop',
    'without a previous pause',
    'previousDescriptor',
    'previous descriptor',
    'descriptor',
    'MusicKit',
    'musickit',
    'js-cdn.music.apple.com',
    'MEDIA_KEY',
    'MediaKeySession',
    'play() request was interrupted',
    'The play() request',
    'AbortError',
    'NotAllowedError',
    'NotSupportedError',
    'A method was called',
    'method was called without',
    'MusicUserToken',
    'missing MusicUserToken',
    'Unable to prepare media item',
    'operation was aborted',
    'operation is aborted',
    'The operation was aborted',
    'Failed to load resource',
    'QUOTA_EXCEEDED_ERR',
    'The request is not allowed'
  ];

  const shouldSuppressError = (message) => {
    if (!message) return false;
    const msgStr = message.toString().toLowerCase();
    return SUPPRESSED_ERRORS.some(err => msgStr.includes(err.toLowerCase()));
  };

  // CRITICAL: Override window.alert to suppress MusicKit error dialogs
  // MusicKit JS internally calls alert() for certain errors (e.g., "The operation was aborted.")
  // which creates a disruptive native browser dialog during live sessions
  const originalAlert = window.alert;
  window.alert = function(message) {
    if (message && shouldSuppressError(message)) {
      console.warn('[MusicKit] Suppressed alert dialog:', message);
      return; // Swallow the alert silently
    }
    // Also suppress any permission/autoplay related alerts
    if (message && typeof message === 'string' && (
      message.includes('denied permission') ||
      message.includes('user agent') ||
      message.includes('NotAllowedError')
    )) {
      console.warn('[MusicKit] Suppressed permission alert:', message);
      return;
    }
    return originalAlert.call(window, message);
  };

  // Global error handler
  const originalWindowError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Suppress MusicKit-related errors
    if (shouldSuppressError(message) || (source && source.includes('musickit')) || (source && source.includes('music.apple.com'))) {
      console.warn('[MusicKit] Suppressed error:', message);
      return true; // Prevent default error handling
    }
    if (originalWindowError) {
      return originalWindowError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Handle unhandled promise rejections from MusicKit
  window.addEventListener('unhandledrejection', function(event) {
    const reason = event.reason;
    if (reason) {
      // Check error.toString(), error.message, and error.name
      const reasonStr = reason.toString();
      const reasonMsg = reason.message || '';
      const reasonName = reason.name || '';
      if (shouldSuppressError(reasonStr) || shouldSuppressError(reasonMsg) || shouldSuppressError(reasonName)) {
        console.warn('[MusicKit] Suppressed unhandled rejection:', reasonMsg || reasonStr);
        event.preventDefault();
        return;
      }
    }
  });

  // Override console.error to suppress MusicKit errors from React Error Overlay
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const message = args.join(' ');
    if (shouldSuppressError(message)) {
      console.warn('[MusicKit] Suppressed console error:', args[0]);
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // Also suppress React's error overlay for these errors
  if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ !== 'undefined') {
    const originalOnCommitFiberRoot = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot;
    if (originalOnCommitFiberRoot) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = function(...args) {
        try {
          return originalOnCommitFiberRoot.apply(this, args);
        } catch (e) {
          if (shouldSuppressError(e.toString())) {
            return;
          }
          throw e;
        }
      };
    }
  }
}

export const useMusicKit = () => {
  const context = useContext(MusicKitContext);
  if (!context) {
    throw new Error('useMusicKit must be used within a MusicKitProvider');
  }
  return context;
};

export const MusicKitProvider = ({ children }) => {
  const [musicKit, setMusicKit] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSong, setCurrentSong] = useState(null);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Ref for playback time polling interval (for lyrics sync)
  const playbackTimePollRef = useRef(null);
  
  // Track the source of music: 'playlist', 'dm', 'session', or null
  const [musicSource, setMusicSource] = useState(null);
  
  // Shuffle and Repeat modes
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState(0); // 0: off, 1: repeat all, 2: repeat one
  
  // Queue state for auto-play
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  
  // CRITICAL: Refs to always have latest queue values for the track-ended callback
  // This prevents stale closure issues where the callback uses old state
  const queueRef = useRef([]);
  const queueIndexRef = useRef(0);
  
  // Flag: when true, MusicKit's native queue handles auto-advance (Party Mode / AutoMix)
  const nativeQueueActiveRef = useRef(false);
  
  // Track manual seeks — suppresses near-end fallback for 3s after a seek
  // (prevents auto-advance when user deliberately seeks near end of song)
  const lastManualSeekRef = useRef(0);
  
  // Callback for when track ends - can be used by SessionView for auto-play next
  const onTrackEndedCallbackRef = useRef(null);
  
  // Ref to always have the latest handleTrackEnded function
  const handleTrackEndedRef = useRef(null);
  
  // Ref to prevent duplicate end-of-song processing
  const isProcessingEndRef = useRef(false);
  // Tracks the active timeout that clears isProcessingEndRef, so rapid
  // playSong/loadSongAtPosition calls cancel the previous timer instead
  // of letting stale timeouts clear the guard prematurely.
  const processingEndTimeoutRef = useRef(null);
  
  // Safety-net timer ref for detecting track end when state events don't fire
  const trackEndMonitorRef = useRef(null);
  
  // Monotonically increasing operation ID for playSong. When a new playSong call
  // arrives before the previous one resolves, the old call detects it has been
  // superseded and bails out, preventing stale side-effects.
  const playSongOpIdRef = useRef(0);
  
  // State to track callback version - increments when callback is cleared
  // This allows PersistentMiniPlayer to detect when it needs to re-register
  const [callbackVersion, setCallbackVersion] = useState(0);
  
  const musicKitRef = useRef(null);
  
  // Track the previous user token to detect logout
  const prevTokenRef = useRef(localStorage.getItem('tapInToken'));

  // CRITICAL: Keep queue refs in sync with state to prevent stale closures
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);
  
  useEffect(() => {
    queueIndexRef.current = queueIndex;
  }, [queueIndex]);

  // CRITICAL: Clear music state when user logs out
  // This watches localStorage for token removal and resets all music state
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'tapInToken' && e.newValue === null && prevTokenRef.current !== null) {
        console.log('[MusicKit] User logged out - clearing all music state');
        // Stop any playing music
        if (musicKitRef.current) {
          try {
            musicKitRef.current.stop();
            musicKitRef.current.pause();
          } catch (err) {
            // Ignore errors during cleanup
          }
        }
        // Reset all state and refs
        setCurrentSong(null);
        setPlaybackTime(0);
        setDuration(0);
        setIsPlaying(false);
        setQueue([]);
        setQueueIndex(0);
        queueRef.current = [];
        queueIndexRef.current = 0;
        setMusicSource(null);
        onTrackEndedCallbackRef.current = null;
        console.log('[MusicKit] Music state cleared on logout');
      }
      // Update the ref to track current token state
      prevTokenRef.current = e.newValue;
    };
    
    // Also check on interval in case storage event doesn't fire (same tab)
    const checkLogout = () => {
      const currentToken = localStorage.getItem('tapInToken');
      if (prevTokenRef.current !== null && currentToken === null) {
        console.log('[MusicKit] Detected logout (same tab) - clearing all music state');
        // Stop any playing music
        if (musicKitRef.current) {
          try {
            musicKitRef.current.stop();
            musicKitRef.current.pause();
          } catch (err) {
            // Ignore errors during cleanup
          }
        }
        // Reset all state and refs
        setCurrentSong(null);
        setPlaybackTime(0);
        setDuration(0);
        setIsPlaying(false);
        setQueue([]);
        setQueueIndex(0);
        queueRef.current = [];
        queueIndexRef.current = 0;
        setMusicSource(null);
        onTrackEndedCallbackRef.current = null;
        console.log('[MusicKit] Music state cleared');
      }
      prevTokenRef.current = currentToken;
    };
    
    window.addEventListener('storage', handleStorageChange);
    const logoutCheckInterval = setInterval(checkLogout, 2000); // Check every 2 seconds instead of 500ms
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(logoutCheckInterval);
    };
  }, []);

  // Initialize MusicKit when the script loads
  useEffect(() => {
    const initMusicKit = async () => {
      try {
        // Wait for MusicKit to be available
        if (typeof window.MusicKit === 'undefined') {
          // MusicKit script not loaded yet, wait and retry
          const checkInterval = setInterval(() => {
            if (typeof window.MusicKit !== 'undefined') {
              clearInterval(checkInterval);
              configureMusicKit();
            }
          }, 100);
          
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            if (typeof window.MusicKit === 'undefined') {
              setError('MusicKit failed to load');
              setIsLoading(false);
            }
          }, 10000);
          return;
        }
        
        await configureMusicKit();
      } catch (err) {
        console.error('MusicKit initialization error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    initMusicKit();
  }, []);

  const configureMusicKit = async () => {
    try {
      // Fetch developer token from backend
      const response = await fetch(`${API_URL}/api/music/token`);
      if (!response.ok) {
        throw new Error('Failed to fetch developer token');
      }
      const { developer_token } = await response.json();

      // Configure MusicKit
      const mk = await window.MusicKit.configure({
        developerToken: developer_token,
        app: {
          name: 'Tap In',
          build: '1.0.0',
        },
      });

      musicKitRef.current = mk;
      setMusicKit(mk);
      setIsConfigured(true);
      setIsLoading(false);
      setError(null); // Clear any "not initialized" error from earlier race conditions

      // CRITICAL: Disable autoplay - the app's session queue controls track advancement
      // MusicKit must NEVER play songs on its own (no Apple Music recommendations)
      mk.autoplayEnabled = false;

      // CRITICAL: Ensure shuffle is OFF for sequential playback
      mk.shuffleMode = 0; // 0 = off, 1 = songs
      
      // CRITICAL: Ensure repeat is OFF - no song should ever loop
      mk.repeatMode = 0; // 0 = none, 1 = one, 2 = all
      
      // Set initial volume to 50% (0.5)
      try {
        if (typeof mk.volume !== 'undefined') {
          mk.volume = 0.5;
        }
        if (mk.player && typeof mk.player.volume !== 'undefined') {
          mk.player.volume = 0.5;
        }
        console.log('[MusicKit] Initial volume set to 0.5');
      } catch (e) {
        console.warn('[MusicKit] Could not set initial volume:', e);
      }
      
      console.log('[MusicKit] Shuffle OFF, Repeat OFF - songs play in order, no looping');

      // Set up event listeners
      // MusicKit's native queue handles auto-play - we just update UI state here
      mk.addEventListener('playbackStateDidChange', async (event) => {
        // Update isPlaying state
        setIsPlaying(mk.isPlaying);
        
        const state = event.state;
        // MusicKit states: 0=none, 1=loading, 2=playing, 3=paused, 4=stopped, 5=ended, 6=seeking, 7=waiting, 8=stalled, 9=completed
        console.log('[MusicKit] playbackStateDidChange:', state, 'isPlaying:', mk.isPlaying);

        // Helper to fire the track-ended callback chain
        const fireTrackEndedCallback = (source) => {
          if (isProcessingEndRef.current) return;
          isProcessingEndRef.current = true;
          // Clear safety-net monitor since we're handling the end now
          if (trackEndMonitorRef.current) {
            clearInterval(trackEndMonitorRef.current);
            trackEndMonitorRef.current = null;
          }
          if (onTrackEndedCallbackRef.current) {
            console.log('[MusicKit] Firing onTrackEndedCallback from:', source);
            onTrackEndedCallbackRef.current();
          } else if (handleTrackEndedRef.current) {
            console.log('[MusicKit] Firing handleTrackEnded from:', source);
            handleTrackEndedRef.current();
          } else {
            console.log('[MusicKit] No callback available for auto-play');
          }
          // Cooldown before allowing another track-ended callback.
          // 3s prevents duplicate fires during MusicKit state transitions
          // (state 5→3→4 cascade). Cancel any prior timeout so rapid
          // operations don't let a stale timer clear the guard early.
          if (processingEndTimeoutRef.current) clearTimeout(processingEndTimeoutRef.current);
          processingEndTimeoutRef.current = setTimeout(() => {
            isProcessingEndRef.current = false;
            processingEndTimeoutRef.current = null;
          }, 3000);
        };
        
        // Check if track ended (state 5=ended or 9=completed)
        if (state === 5 || state === 9) {
          console.log('[MusicKit] Track ended (state:', state, ')');
          
          if (!nativeQueueActiveRef.current) {
            mk.autoplayEnabled = false;
          }
          
          // When native queue is active, trust MusicKit auto-advance
          if (nativeQueueActiveRef.current) {
            console.log('[MusicKit] Native queue active - trusting MusicKit auto-advance');
            mk.autoplayEnabled = false; // Never auto-play random songs after queue ends
            return;
          }
          
          fireTrackEndedCallback('state-5/9');
        }
        
        // FALLBACK: Detect paused/stopped near end of track
        // Some mobile browsers don't fire state 5/9 reliably
        if (state === 3 || state === 4) {
          const currentTime = mk.currentPlaybackTime || 0;
          const duration = mk.currentPlaybackDuration || 0;
          // Widen the window from 1.5s to 3s to catch more edge cases
          if (duration > 0 && currentTime > 0 && (duration - currentTime) < 3) {
            // Skip near-end fallback if user just seeked (they may be deliberately near end)
            // State 5/9 will still handle the actual track end correctly
            if (lastManualSeekRef.current && (Date.now() - lastManualSeekRef.current) < 3000) {
              console.log('[MusicKit] Near-end after recent seek — skipping fallback');
              return;
            }
            if (nativeQueueActiveRef.current) {
              mk.autoplayEnabled = false; // Never auto-play random songs
              return;
            }
            console.log('[MusicKit] Track paused/stopped near end (', currentTime.toFixed(1), '/', duration.toFixed(1), ')');
            fireTrackEndedCallback('near-end-fallback');
          }
        }

        // When playback starts, start a safety-net monitor
        // NOTE: Do NOT clear isProcessingEndRef here — the 3-second timeout in
        // fireTrackEndedCallback is the sole mechanism to clear the dedup lock.
        // Clearing it on state 2 caused a race condition: during song transitions,
        // MusicKit can briefly emit state 3/4 with STALE metadata (old song's
        // time near duration), which falsely triggers fireTrackEndedCallback a
        // second time, sending skip_next twice and skipping a song.
        if (state === 2) {
          // Clear any existing monitor
          if (trackEndMonitorRef.current) {
            clearInterval(trackEndMonitorRef.current);
            trackEndMonitorRef.current = null;
          }
          // Start safety-net: poll every 1s to detect if track ended without state event
          if (!nativeQueueActiveRef.current) {
            trackEndMonitorRef.current = setInterval(() => {
              const t = mk.currentPlaybackTime || 0;
              const d = mk.currentPlaybackDuration || 0;
              // If track has a duration, time is near end, and MusicKit is no longer playing
              if (d > 5 && t > 0 && (d - t) < 1 && !mk.isPlaying) {
                console.log('[MusicKit] Safety-net: detected track end (', t.toFixed(1), '/', d.toFixed(1), ')');
                clearInterval(trackEndMonitorRef.current);
                trackEndMonitorRef.current = null;
                fireTrackEndedCallback('safety-net-monitor');
              }
              // Also detect if playback stopped entirely (time stuck at 0 after being > 0)
              if (d > 5 && t === 0 && !mk.isPlaying && mk.playbackState !== 1 && mk.playbackState !== 6) {
                // MusicKit reset time — check if this is a genuine end
                const ps = mk.playbackState; // 0=none, 4=stopped, 5=ended
                if (ps === 0 || ps === 4 || ps === 5) {
                  console.log('[MusicKit] Safety-net: playback stopped/reset (state:', ps, ')');
                  clearInterval(trackEndMonitorRef.current);
                  trackEndMonitorRef.current = null;
                  fireTrackEndedCallback('safety-net-stopped');
                }
              }
            }, 1000);
          }
        }
        
        // Clean up monitor when playback stops completely
        if (state === 0 || state === 4) {
          if (trackEndMonitorRef.current && !isProcessingEndRef.current) {
            // Don't clear immediately — give the callback a chance to fire
            setTimeout(() => {
              if (trackEndMonitorRef.current && !isProcessingEndRef.current) {
                const t = mk.currentPlaybackTime || 0;
                const d = mk.currentPlaybackDuration || 0;
                if (d > 5 && (d - t) < 3) {
                  fireTrackEndedCallback('cleanup-near-end');
                }
              }
            }, 500);
          }
        }
      });

      // Track song changes - for UI updates
      // MusicKit automatically advances through the queue
      mk.addEventListener('nowPlayingItemDidChange', (event) => {
        const item = mk.nowPlayingItem;
        console.log('[MusicKit] nowPlayingItemDidChange - item:', item?.id, item?.attributes?.name);
        
        if (item) {
          const songName = item.attributes?.name || '';
          const artistName = item.attributes?.artistName || '';
          
          setCurrentSong({
            id: item.id,
            name: songName,
            artistName: artistName,
            albumName: item.attributes?.albumName || '',
            artworkUrl: item.attributes?.artwork?.url?.replace('{w}', '300').replace('{h}', '300') || '',
            durationMs: item.attributes?.durationInMillis || 0,
          });
          setDuration(item.attributes?.durationInMillis / 1000 || 0);
          
          // CRITICAL: Sync queue index when MusicKit natively advances to a new song
          // This keeps queueIndexRef accurate for handleTrackEnded
          const currentQueue = queueRef.current;
          if (currentQueue.length > 0) {
            const newIndex = currentQueue.findIndex(q => q.id === item.id);
            if (newIndex !== -1 && newIndex !== queueIndexRef.current) {
              console.log('[MusicKit] Queue index synced:', queueIndexRef.current, '->', newIndex);
              queueIndexRef.current = newIndex;
              setQueueIndex(newIndex);
            }
          }
          
          // PREFETCH LYRICS IMMEDIATELY when song changes
          if (songName && artistName) {
            console.log('[MusicKit] Prefetching lyrics for:', songName, 'by', artistName);
            prefetchLyrics(songName, artistName);
          }
        } else {
          setCurrentSong(null);
          setDuration(0);
        }
      });

      // Track playback time - for progress bar UI and lyrics sync
      // CRITICAL: Event might not fire frequently enough for lyrics, so also poll
      mk.addEventListener('playbackTimeDidChange', (event) => {
        const currentTime = mk.currentPlaybackTime || 0;
        setPlaybackTime(currentTime);
      });
      
      // Polling fallback for playback time - ensures lyrics stay synced
      // Store in ref so it can be cleaned up
      if (playbackTimePollRef.current) {
        clearInterval(playbackTimePollRef.current);
      }
      playbackTimePollRef.current = setInterval(() => {
        if (mk && mk.playbackState === window.MusicKit.PlaybackStates.playing) {
          const currentTime = mk.currentPlaybackTime || 0;
          setPlaybackTime(currentTime);
        }
      }, 250); // Poll every 250ms for smooth lyrics sync
      
      // Track volume changes - sync UI with actual volume
      // ONLY use event listener - NO POLLING to prevent UI jitter
      mk.addEventListener('playbackVolumeDidChange', (event) => {
        const newVolume = mk.volume;
        console.log('[MusicKit] Volume changed to:', newVolume);
        setVolume(newVolume);
      });

      // Listen for authorization status changes
      // This is critical - without it, auth state can get out of sync
      mk.addEventListener('authorizationStatusDidChange', (event) => {
        const newAuthStatus = mk.isAuthorized;
        console.log('[MusicKit] Authorization status changed:', newAuthStatus);
        setIsAuthorized(newAuthStatus);
        
        if (!newAuthStatus) {
          console.log('[MusicKit] User logged out of Apple Music');
        }
      });

      // Listen for user token changes (can happen if token refreshes)
      mk.addEventListener('userTokenDidChange', (event) => {
        console.log('[MusicKit] User token changed, checking auth status...');
        const newAuthStatus = mk.isAuthorized;
        console.log('[MusicKit] New auth status after token change:', newAuthStatus);
        setIsAuthorized(newAuthStatus);
      });

      // Check if already authorized
      console.log('[MusicKit] Checking authorization status:', mk.isAuthorized);
      if (mk.isAuthorized) {
        console.log('[MusicKit] User is already authorized with Apple Music');
        setIsAuthorized(true);
        // Remember that user has connected Apple Music
        localStorage.setItem('tapInAppleMusicConnected', 'true');
      } else {
        console.log('[MusicKit] User is NOT authorized with Apple Music');
        // Check if user was previously connected - if so, try to silently re-authorize
        const wasConnected = localStorage.getItem('tapInAppleMusicConnected') === 'true';
        if (wasConnected) {
          console.log('[MusicKit] User was previously connected, attempting silent re-auth...');
          // Don't auto-authorize - just log it. User will need to click connect again.
          // This prevents popup spam
        }
      }

      console.log('MusicKit configured successfully');
    } catch (err) {
      console.error('MusicKit configuration error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  // BACKGROUND RECOVERY: When user returns to the app after screen lock/background,
  // check if MusicKit stalled and recover playback
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const mk = musicKitRef.current;
      if (!mk) return;
      
      // Sync UI state with MusicKit's current state
      setIsPlaying(mk.isPlaying);
      
      if (mk.nowPlayingItem) {
        const item = mk.nowPlayingItem;
        setCurrentSong({
          id: item.id,
          name: item.attributes?.name || '',
          artistName: item.attributes?.artistName || '',
          albumName: item.attributes?.albumName || '',
          artworkUrl: item.attributes?.artwork?.url?.replace('{w}', '300').replace('{h}', '300') || '',
          durationMs: item.attributes?.durationInMillis || 0,
        });
        
        // Sync queue index based on what MusicKit is currently playing
        const currentQueue = queueRef.current;
        if (currentQueue.length > 0) {
          const newIndex = currentQueue.findIndex(q => q.id === item.id);
          if (newIndex !== -1 && newIndex !== queueIndexRef.current) {
            console.log('[MusicKit] Background recovery: queue index synced', queueIndexRef.current, '->', newIndex);
            queueIndexRef.current = newIndex;
            setQueueIndex(newIndex);
          }
        }
      }
      
      // For native queue: if playback ended while backgrounded, keep autoplay off
      if (nativeQueueActiveRef.current && !mk.isPlaying) {
        mk.autoplayEnabled = false;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Authorize user with Apple Music
  // State for Apple Music duplicate detection
  const [appleMusicError, setAppleMusicError] = useState(null);
  const [linkedUsername, setLinkedUsername] = useState(null);

  const authorize = useCallback(async () => {
    if (!musicKitRef.current) {
      setError('MusicKit not initialized');
      return false;
    }

    try {
      const userToken = await musicKitRef.current.authorize();
      console.log('[MusicKit] Apple authorization successful, got user token');
      
      // STRICT POLICY: Validate Apple Music account is not already linked
      const token = localStorage.getItem('tapInToken');
      if (token) {
        try {
          console.log('[MusicKit] Validating Apple Music with backend...');
          const response = await fetch(`${API_URL}/api/auth/apple-music/connect`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ music_user_token: userToken })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            console.log('[MusicKit] Backend response error:', errorData);
            if (errorData.detail?.error === 'apple_music_already_linked') {
              // Apple Music already linked to another account
              console.error('[MusicKit] Apple Music already linked to:', errorData.detail.username);
              setAppleMusicError('already_linked');
              setLinkedUsername(errorData.detail.username);
              // Unauthorize since we can't use this account
              await musicKitRef.current.unauthorize();
              return { success: false, error: 'already_linked', username: errorData.detail.username };
            }
            // For other errors, log but don't unauthorize - let user keep playing
            console.warn('[MusicKit] Backend validation failed but allowing playback:', errorData);
          } else {
            console.log('[MusicKit] Apple Music linked successfully to this account');
          }
        } catch (apiError) {
          console.error('[MusicKit] Error validating Apple Music (network?):', apiError);
          // Continue with authorization even if API fails - user can still play music
        }
      }
      
      setIsAuthorized(true);
      setError(null);
      setAppleMusicError(null);
      setLinkedUsername(null);
      // Remember that user has connected Apple Music
      localStorage.setItem('tapInAppleMusicConnected', 'true');
      console.log('[MusicKit] Authorization complete - user is now authorized');
      return { success: true };
    } catch (err) {
      console.error('Authorization error:', err);
      setError('Failed to authorize with Apple Music');
      return { success: false, error: 'auth_failed' };
    }
  }, []);

  // Clear Apple Music error
  const clearAppleMusicError = useCallback(() => {
    setAppleMusicError(null);
    setLinkedUsername(null);
  }, []);

  // Unauthorize (sign out)
  const unauthorize = useCallback(async () => {
    if (!musicKitRef.current) return;

    try {
      await musicKitRef.current.unauthorize();
      setIsAuthorized(false);
      // Clear the connected state
      localStorage.removeItem('tapInAppleMusicConnected');
    } catch (err) {
      console.error('Unauthorize error:', err);
    }
  }, []);

  // Play a song by ID - always starts from beginning
  const playSong = useCallback(async (songId) => {
    // Assign a unique operation ID so concurrent calls can detect supersession.
    const opId = ++playSongOpIdRef.current;
    console.log('[MusicKit] playSong called with songId:', songId, 'opId:', opId);
    console.log('[MusicKit] State - isConfigured:', isConfigured, 'isAuthorized:', isAuthorized);
    
    if (!musicKitRef.current) {
      console.error('[MusicKit] ERROR: MusicKit not initialized');
      return false;
    }
    
    if (!songId) {
      console.error('[MusicKit] ERROR: No songId provided');
      return false;
    }

    // Check authorization FIRST - don't try to play without it
    if (!isAuthorized) {
      console.log('[MusicKit] Not authorized - user needs to connect Apple Music');
      // Set a flag that the UI can use to show the connect modal
      setError({ type: 'AUTH_REQUIRED', message: 'Please connect Apple Music to play songs' });
      return false;
    }

    try {
      console.log('[MusicKit] Setting queue with song:', songId);
      // Reset native queue flag - single song playback
      nativeQueueActiveRef.current = false;
      // CRITICAL: Disable autoplay for single-song mode
      // The app's session queue handles track advancement, NOT MusicKit
      musicKitRef.current.autoplayEnabled = false;
      
      // Proactively check the actual MusicUserToken before calling setQueue.
      // MusicKit's isAuthorized flag can be true even when the token has expired.
      // If we call setQueue/play with an expired token, MusicKit's native iOS layer
      // shows a system dialog ("Unable to prepare media item for play: missing
      // MusicUserToken.") that cannot be suppressed via window.alert overrides.
      if (!musicKitRef.current.musicUserToken) {
        console.warn('[MusicKit] MusicUserToken is missing/expired — prompting re-auth');
        setError({ type: 'AUTH_REQUIRED', message: 'Please reconnect Apple Music to play songs' });
        return false;
      }
      
      // Block false track-end callbacks during song transition.
      // When playSong is called (manual skip/play), mk.setQueue stops the old song,
      // causing playbackStateDidChange to fire state 5/3/4 which looks like a
      // natural track end. Without this guard, fireTrackEndedCallback would
      // send an extra skip_next, causing double-advance.
      isProcessingEndRef.current = true;
      if (trackEndMonitorRef.current) {
        clearInterval(trackEndMonitorRef.current);
        trackEndMonitorRef.current = null;
      }
      if (processingEndTimeoutRef.current) clearTimeout(processingEndTimeoutRef.current);
      processingEndTimeoutRef.current = setTimeout(() => {
        isProcessingEndRef.current = false;
        processingEndTimeoutRef.current = null;
      }, 3000);
      
      await musicKitRef.current.setQueue({ songs: [songId] });
      
      // Check if this operation was superseded by a newer playSong call
      // (e.g. user clicked skip again while setQueue was in-flight).
      // If so, bail out — the newer call owns MusicKit now.
      if (playSongOpIdRef.current !== opId) {
        console.log('[MusicKit] playSong superseded (opId', opId, 'vs current', playSongOpIdRef.current, ') — bailing');
        return false;
      }
      
      console.log('[MusicKit] Queue set, attempting to play...');
      
      try {
        await musicKitRef.current.play();
        console.log('[MusicKit] Play command sent successfully');
      } catch (playErr) {
        // If superseded during play(), bail silently
        if (playSongOpIdRef.current !== opId) {
          console.log('[MusicKit] playSong superseded during play() — bailing');
          return false;
        }
        // Check for MusicUserToken error - means auth is actually invalid
        if (playErr?.message?.includes('MusicUserToken') || playErr?.message?.includes('Unable to prepare media item')) {
          console.warn('[MusicKit] Auth token invalid - need to reconnect');
          setError({ type: 'AUTH_REQUIRED', message: 'Please reconnect Apple Music to play songs' });
          return false;
        }
        // Suppress abort errors from superseded operations
        if (playErr?.name === 'AbortError' || playErr?.message?.includes('aborted')) {
          console.warn('[MusicKit] Play aborted (likely superseded):', playErr?.message);
          return false;
        }
        // Suppress harmless errors
        if (!playErr?.message?.includes('without a previous') &&
            !playErr?.message?.includes('descriptor')) {
          console.warn('[MusicKit] Play error (non-fatal):', playErr?.message);
        }
      }
      
      // Final supersession check before declaring success
      if (playSongOpIdRef.current !== opId) {
        console.log('[MusicKit] playSong superseded after play() — not reporting success');
        return false;
      }
      
      setError(null);
      return true;
    } catch (err) {
      // If superseded, bail silently — the error is from the interrupted operation
      if (playSongOpIdRef.current !== opId) {
        console.log('[MusicKit] playSong error from superseded operation — ignoring');
        return false;
      }
      
      console.error('[MusicKit] Play error:', err);
      
      // Suppress abort errors gracefully
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        console.warn('[MusicKit] setQueue/play aborted:', err?.message);
        return false;
      }
      
      // Check for MusicUserToken error
      if (err.message?.includes('MusicUserToken') || err.message?.includes('Unable to prepare media item')) {
        console.warn('[MusicKit] Auth token invalid - need to reconnect');
        setError({ type: 'AUTH_REQUIRED', message: 'Please reconnect Apple Music to play songs' });
        return false;
      }
      
      // For unavailable songs, just log and return false
      if (err.message?.includes('Unable to complete request') || 
          err.message?.includes('CONTENT_UNAVAILABLE') ||
          err.message?.includes('not available')) {
        console.warn('[MusicKit] Song unavailable');
        return false;
      }
      
      // Log other errors but don't show modal
      console.warn('[MusicKit] Playback issue:', err.message);
      return false;
    }
  }, [isConfigured, isAuthorized]);

  // Play a song with upcoming songs pre-queued for background/locked screen playback
  // MusicKit will auto-advance through the queue even when JS is suspended
  const playSongWithUpcoming = useCallback(async (songId, upcomingSongIds = []) => {
    console.log('[MusicKit] playSongWithUpcoming:', songId, 'upcoming:', upcomingSongIds?.length);
    
    if (!musicKitRef.current || !songId) return false;
    if (!isAuthorized) {
      setError({ type: 'AUTH_REQUIRED', message: 'Please connect Apple Music to play songs' });
      return false;
    }

    try {
      const mk = musicKitRef.current;
      const allSongIds = [songId, ...upcomingSongIds].filter(Boolean);
      
      // Set up queue with current + upcoming songs
      nativeQueueActiveRef.current = allSongIds.length > 1;
      mk.autoplayEnabled = false; // NEVER auto-play random songs after playlist ends
      mk.shuffleMode = 0;
      mk.repeatMode = 0;
      
      // Update internal queue
      const queueItems = allSongIds.map(id => ({ id }));
      queueRef.current = queueItems;
      queueIndexRef.current = 0;
      setQueue(queueItems);
      setQueueIndex(0);
      
      await mk.setQueue({ songs: allSongIds });
      mk.autoplayEnabled = false; // Ensure it stays off after setQueue
      
      try {
        await mk.play();
      } catch (playErr) {
        if (playErr?.name === 'NotAllowedError' || playErr?.message?.includes('not allowed')) {
          console.warn('[MusicKit] Autoplay blocked — user gesture required');
        } else {
          console.warn('[MusicKit] Play warning:', playErr?.message);
        }
      }
      
      setError(null);
      return true;
    } catch (err) {
      console.error('[MusicKit] playSongWithUpcoming error:', err);
      return false;
    }
  }, [isAuthorized]);

  // Load song at a specific position - simple and direct approach
  // Prioritizes RELIABILITY over silent sync
  const loadSongAtPosition = useCallback(async (songId, targetPositionSeconds) => {
    console.log('[MusicKit] loadSongAtPosition called - songId:', songId, 'position:', targetPositionSeconds);
    
    if (!musicKitRef.current) {
      console.error('[MusicKit] ERROR: MusicKit not initialized');
      return false;
    }
    
    if (!isConfigured || !isAuthorized) {
      console.error('[MusicKit] ERROR: Not configured or authorized');
      return false;
    }

    if (!songId) {
      console.error('[MusicKit] ERROR: No songId provided');
      return false;
    }

    const mk = musicKitRef.current;

    try {
      // CRITICAL: Disable autoplay so MusicKit doesn't play random recommendations
      // The app's session queue handles track advancement, NOT MusicKit
      nativeQueueActiveRef.current = false;
      mk.autoplayEnabled = false;

      // Proactively check the actual MusicUserToken before calling setQueue.
      // Same rationale as in playSong — prevents the native iOS dialog.
      if (!mk.musicUserToken) {
        console.warn('[MusicKit] MusicUserToken is missing/expired in loadSongAtPosition — prompting re-auth');
        setError({ type: 'AUTH_REQUIRED', message: 'Please reconnect Apple Music to play songs' });
        return false;
      }

      // Block false track-end callbacks during song transition (same as playSong).
      isProcessingEndRef.current = true;
      if (trackEndMonitorRef.current) {
        clearInterval(trackEndMonitorRef.current);
        trackEndMonitorRef.current = null;
      }
      if (processingEndTimeoutRef.current) clearTimeout(processingEndTimeoutRef.current);
      processingEndTimeoutRef.current = setTimeout(() => {
        isProcessingEndRef.current = false;
        processingEndTimeoutRef.current = null;
      }, 3000);

      // Step 1: Set queue with the song
      console.log('[MusicKit] Setting queue with song:', songId);
      await mk.setQueue({ songs: [songId] });
      
      // Step 2: Start playback
      try {
        await mk.play();
        console.log('[MusicKit] Playback started');
      } catch (e) {
        // Autoplay blocked by browser — return false so caller can show tap-to-start overlay
        if (e?.name === 'NotAllowedError' || e?.message?.includes('not allowed') || e?.message?.includes('user denied')) {
          console.warn('[MusicKit] Autoplay blocked in loadSongAtPosition:', e?.message);
          return false;
        }
        // Suppress harmless MusicKit errors, log others
        if (!e?.message?.includes('without a previous') && !e?.message?.includes('descriptor')) {
          console.warn('[MusicKit] Play warning:', e?.message);
        }
      }
      
      // Verify playback actually started (catches silent autoplay blocks on mobile)
      // States: 0=none, 1=loading, 2=playing, 3=paused, 4=stopped
      // States 1,2,6,7,8 mean play was accepted; 0,3,4 mean it failed
      await new Promise(r => setTimeout(r, 100));
      const postPlayState = mk.playbackState;
      if (postPlayState === 0 || postPlayState === 3 || postPlayState === 4) {
        console.warn('[MusicKit] Playback did not start (state:', postPlayState, ') — autoplay likely blocked');
        return false;
      }
      
      // Step 3: Seek to target position if needed
      if (targetPositionSeconds > 0.5) {
        // Wait for playback to start
        await new Promise(r => setTimeout(r, 200));
        
        // Seek to position
        try {
          await mk.seekToTime(targetPositionSeconds);
          console.log('[MusicKit] Seeked to:', targetPositionSeconds);
        } catch (e) {
          console.warn('[MusicKit] Seek warning:', e?.message);
        }
        
        // Verify and retry if needed
        await new Promise(r => setTimeout(r, 150));
        const currentPos = mk.currentPlaybackTime || 0;
        if (Math.abs(currentPos - targetPositionSeconds) > 2) {
          try {
            await mk.seekToTime(targetPositionSeconds);
          } catch (e) {}
        }
      }
      
      setError(null);
      return true;
    } catch (err) {
      console.error('[MusicKit] loadSongAtPosition error:', err);
      
      // Check for MusicUserToken error
      if (err.message?.includes('MusicUserToken') || err.message?.includes('Unable to prepare media item')) {
        console.warn('[MusicKit] Auth token invalid in loadSongAtPosition - need to reconnect');
        setError({ type: 'AUTH_REQUIRED', message: 'Please reconnect Apple Music to play songs' });
      }
      
      return false;
    }
  }, [isConfigured, isAuthorized]);

  // Play multiple songs (queue) - STRICT SEQUENTIAL ORDER
  const playQueue = useCallback(async (songIds) => {
    console.log('[MusicKit] playQueue called with', songIds?.length, 'songs');
    
    if (!musicKitRef.current) {
      console.error('[MusicKit] ERROR: MusicKit not initialized');
      return false;
    }

    if (!songIds || songIds.length === 0) {
      console.error('[MusicKit] ERROR: No song IDs provided');
      return false;
    }

    try {
      // Try to authorize if needed - but don't block playback on failure
      if (!isAuthorized) {
        console.log('[MusicKit] Not authorized, attempting auth...');
        try {
          await authorize();
        } catch (authErr) {
          console.warn('[MusicKit] Auth failed, trying to play anyway:', authErr);
        }
      }

      console.log('[MusicKit] Setting up queue with', songIds.length, 'songs in STRICT ORDER');
      
      // CRITICAL: Mark native queue as active so event handlers don't interfere
      nativeQueueActiveRef.current = true;
      
      // CRITICAL: Update internal queue state AND refs
      const queueItems = songIds.map(id => ({ id }));
      queueRef.current = queueItems;
      queueIndexRef.current = 0;
      setQueue(queueItems);
      setQueueIndex(0);
      
      // CRITICAL: Configure MusicKit for seamless auto-advance
      musicKitRef.current.autoplayEnabled = false; // Never auto-play random songs after queue ends
      musicKitRef.current.shuffleMode = 0; // Off - play in order
      musicKitRef.current.repeatMode = 0;  // No repeat
      
      // Set up the queue with ALL songs - MusicKit auto-advances natively
      await musicKitRef.current.setQueue({ songs: songIds });
      
      // Re-enforce settings after queue is set (MusicKit can reset them)
      musicKitRef.current.autoplayEnabled = false;
      musicKitRef.current.shuffleMode = 0;
      musicKitRef.current.repeatMode = 0;
      
      // Start playback - wrap in try/catch to suppress harmless errors
      try {
        await musicKitRef.current.play();
      } catch (playErr) {
        // Suppress harmless errors and autoplay blocks
        if (playErr?.name === 'NotAllowedError' || playErr?.message?.includes('not allowed')) {
          console.warn('[MusicKit] Autoplay blocked by browser — user gesture required');
        } else if (playErr?.message?.includes('without a previous') ||
            playErr?.message?.includes('descriptor')) {
          console.log('[MusicKit] Play warning suppressed');
        } else {
          console.warn('[MusicKit] Play error (non-fatal):', playErr?.message);
        }
      }
      
      console.log('[MusicKit] Queue started - songs will play in order');
      setError(null);
      return true;
    } catch (err) {
      console.error('[MusicKit] Play queue error:', err);
      // Don't set error for harmless warnings
      if (!err?.message?.includes('without a previous') && 
          !err?.message?.includes('descriptor')) {
        // Only log, don't show error modal
        console.warn('[MusicKit] Queue setup issue:', err?.message);
      }
      return false;
    }
  }, [isAuthorized, authorize]);

  // Playback controls
  const play = useCallback(async () => {
    if (!musicKitRef.current) return;
    try {
      const mk = musicKitRef.current;
      // If already playing, no-op
      if (mk.playbackState === 2) return;
      // If stuck in transitional state (loading/seeking/waiting/stalled), stop first then play
      if ([1, 6, 7, 8].includes(mk.playbackState)) {
        console.log('[MusicKit] play() - clearing transitional state:', mk.playbackState);
        try { await mk.pause(); } catch {}
        await new Promise(r => setTimeout(r, 100));
      }
      // Wrap play() with a timeout to prevent hanging
      await Promise.race([
        mk.play(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('play() timed out')), 5000))
      ]);
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.message?.includes('not allowed') || err?.message?.includes('user denied')) {
        console.warn('[MusicKit] play() blocked by autoplay policy');
        return false;
      }
      if (err?.message?.includes('without a previous stop') || err?.message?.includes('without a previous pause')) {
        console.log('[MusicKit] Play called while already playing - ignoring');
        return;
      }
      if (err?.message === 'play() timed out') {
        console.warn('[MusicKit] play() timed out after 5s - forcing state update');
        setIsPlaying(musicKitRef.current?.isPlaying ?? false);
        return;
      }
      console.error('Play error:', err);
    }
  }, []);

  const pause = useCallback(async () => {
    console.log('[MusicKit] pause() called');
    
    // FALLBACK FIRST: Pause any HTML5 audio/video elements directly
    // This ensures audio stops even if MusicKit has issues
    const mediaElements = document.querySelectorAll('audio, video');
    mediaElements.forEach((media, index) => {
      if (!media.paused) {
        console.log(`[MusicKit] Pausing media element ${index} directly`);
        media.pause();
      }
    });
    
    if (!musicKitRef.current) {
      console.log('[MusicKit] pause() - no MusicKit instance, but media elements paused');
      setIsPlaying(false);
      return;
    }
    
    try {
      console.log('[MusicKit] pause() - calling musicKitRef.current.pause()');
      await musicKitRef.current.pause();
      console.log('[MusicKit] pause() - successfully paused');
      setIsPlaying(false);
    } catch (err) {
      console.error('[MusicKit] Pause error:', err);
      // Still update state since we paused media elements directly
      setIsPlaying(false);
    }
  }, []);

  // Stop playback completely (for session exit) - CLEARS EVERYTHING
  const stop = useCallback(async () => {
    console.log('[MusicKit] STOP called - clearing all playback');
    nativeQueueActiveRef.current = false;
    // Clear safety-net monitor
    if (trackEndMonitorRef.current) {
      clearInterval(trackEndMonitorRef.current);
      trackEndMonitorRef.current = null;
    }
    // CRITICAL: Disable autoplay on stop so MusicKit never plays random songs
    if (musicKitRef.current) {
      musicKitRef.current.autoplayEnabled = false;
    }
    if (!musicKitRef.current) {
      console.log('[MusicKit] No instance to stop');
      // Still reset state and refs
      setCurrentSong(null);
      setPlaybackTime(0);
      setDuration(0);
      setIsPlaying(false);
      setQueue([]);
      setQueueIndex(0);
      queueRef.current = [];
      queueIndexRef.current = 0;
      return;
    }
    try {
      // Stop playback - wrap in try/catch since MusicKit can throw
      try {
        await musicKitRef.current.stop();
        console.log('[MusicKit] MusicKit.stop() completed');
      } catch (stopErr) {
        console.warn('[MusicKit] MusicKit.stop() error (continuing):', stopErr?.message);
      }
      
      // Pause as fallback in case stop didn't work
      try {
        await musicKitRef.current.pause();
        console.log('[MusicKit] MusicKit.pause() completed as backup');
      } catch (pauseErr) {
        // Ignore pause errors
      }
      
      // Clear the queue if possible
      try {
        if (musicKitRef.current.queue) {
          musicKitRef.current.queue.items = [];
        }
      } catch (qErr) {
        console.log('[MusicKit] Could not clear queue:', qErr);
      }
      
      // Reset all state and refs
      setCurrentSong(null);
      setPlaybackTime(0);
      setDuration(0);
      setIsPlaying(false);
      setQueue([]);
      setQueueIndex(0);
      queueRef.current = [];
      queueIndexRef.current = 0;
      
      console.log('[MusicKit] Playback STOPPED and state cleared');
    } catch (err) {
      console.error('[MusicKit] Stop error:', err);
      // Still try to reset state even if stop fails
      setCurrentSong(null);
      setPlaybackTime(0);
      setDuration(0);
      setIsPlaying(false);
      setQueue([]);
      setQueueIndex(0);
      queueRef.current = [];
      queueIndexRef.current = 0;
    }
  }, []);

  const togglePlayPause = useCallback(async () => {
    if (!musicKitRef.current) return;
    if (musicKitRef.current.isPlaying) {
      await pause();
    } else {
      await play();
    }
  }, [play, pause]);

  const skipToNext = useCallback(async () => {
    if (!musicKitRef.current) return;
    try {
      await musicKitRef.current.skipToNextItem();
    } catch (err) {
      console.error('Skip next error:', err);
    }
  }, []);

  const skipToPrevious = useCallback(async () => {
    if (!musicKitRef.current) return;
    try {
      await musicKitRef.current.skipToPreviousItem();
    } catch (err) {
      console.error('Skip previous error:', err);
    }
  }, []);

  const seekTo = useCallback(async (time) => {
    if (!musicKitRef.current) return;
    
    // Validate time is a finite number to prevent "non-finite" errors
    if (typeof time !== 'number' || !Number.isFinite(time) || time < 0) {
      console.warn('[MusicKit] Invalid seek time, defaulting to 0:', time);
      time = 0;
    }
    
    // Record seek timestamp — suppresses near-end fallback for 3s
    // (prevents auto-advance when user deliberately seeks near end)
    lastManualSeekRef.current = Date.now();
    
    // Remember if playback was active before seeking.
    // MusicKit's seekToTime resolves BEFORE its internal state machine finishes
    // (playing→seeking→paused→playing). Checking isPlaying immediately after
    // seekToTime is unreliable — it may still read true while MusicKit is about
    // to transition to paused. The 250ms delay lets the state settle so the
    // resume check is accurate. On iOS, MusicKit often does NOT auto-resume
    // after seekToTime due to DRM re-buffering.
    const wasPlaying = musicKitRef.current.isPlaying;
    
    try {
      console.log('[MusicKit] Seeking to:', time, 'wasPlaying:', wasPlaying);
      await musicKitRef.current.seekToTime(time);
      
      // Resume playback if it was playing before the seek
      if (wasPlaying) {
        // Wait for MusicKit's state machine to settle
        await new Promise(r => setTimeout(r, 250));
        if (!musicKitRef.current.isPlaying) {
          console.log('[MusicKit] Resuming playback after seek');
          try {
            await musicKitRef.current.play();
          } catch (resumeErr) {
            // Suppress harmless resume errors (autoplay blocked, etc.)
            if (!resumeErr?.message?.includes('aborted')) {
              console.warn('[MusicKit] Resume after seek warning:', resumeErr?.message);
            }
          }
        }
      }
    } catch (err) {
      console.error('Seek error:', err);
    }
  }, []);

  // Fetch preview URL for a song from Apple Music catalog
  const getPreviewUrl = useCallback(async (songId) => {
    if (!musicKitRef.current || !songId) return null;
    try {
      const result = await musicKitRef.current.api.music(`/v1/catalog/us/songs/${songId}`);
      const song = result?.data?.data?.[0];
      const previewUrl = song?.attributes?.previews?.[0]?.url;
      console.log('[MusicKit] Preview URL for', songId, ':', previewUrl ? 'found' : 'not found');
      return previewUrl || null;
    } catch (err) {
      console.warn('[MusicKit] Failed to get preview URL:', err);
      return null;
    }
  }, []);


  const setPlayerVolume = useCallback((vol) => {
    console.log('[MusicKit] setPlayerVolume called with:', vol);
    if (!musicKitRef.current) {
      console.warn('[MusicKit] Cannot set volume - MusicKit not initialized');
      return;
    }
    
    // Ensure volume is between 0 and 1
    const normalizedVol = Math.max(0, Math.min(1, vol));
    console.log('[MusicKit] Normalized volume:', normalizedVol);
    
    // MusicKit JS v3 uses different volume paths - try multiple approaches
    try {
      // Method 1: Direct volume property on MusicKit instance
      if (typeof musicKitRef.current.volume !== 'undefined') {
        musicKitRef.current.volume = normalizedVol;
        console.log('[MusicKit] Set volume via .volume:', normalizedVol);
      }
      
      // Method 2: player.volume (MusicKit v1/v2 style)
      if (musicKitRef.current.player && typeof musicKitRef.current.player.volume !== 'undefined') {
        musicKitRef.current.player.volume = normalizedVol;
        console.log('[MusicKit] Set volume via .player.volume:', normalizedVol);
      }
      
      // Method 3: Global MusicKit instance
      if (window.MusicKit && window.MusicKit.getInstance) {
        const instance = window.MusicKit.getInstance();
        if (instance) {
          if (typeof instance.volume !== 'undefined') {
            instance.volume = normalizedVol;
          }
          if (instance.player && typeof instance.player.volume !== 'undefined') {
            instance.player.volume = normalizedVol;
          }
          console.log('[MusicKit] Set volume via getInstance():', normalizedVol);
        }
      }
      
      // Method 4: CRITICAL for mobile - Set volume on any audio/video elements directly
      // MusicKit creates audio elements that handle actual playback
      const mediaElements = document.querySelectorAll('audio, video');
      mediaElements.forEach((media, index) => {
        media.volume = normalizedVol;
        console.log(`[MusicKit] Set volume on media element ${index}:`, normalizedVol);
      });
      
      // Update React state
      setVolume(normalizedVol);
      console.log('[MusicKit] Volume set successfully to:', normalizedVol);
    } catch (err) {
      console.error('[MusicKit] Error setting volume:', err);
    }
  }, []);

  // Handle track ended - auto-play next based on repeat mode
  // This is the INTERNAL handler - SessionView's callback takes priority when set
  // CRITICAL: Uses refs instead of state to avoid stale closure issues
  const handleTrackEnded = useCallback(async () => {
    console.log('[MusicKit] Internal handleTrackEnded called');
    console.log('[MusicKit] Queue from ref:', queueRef.current?.length, 'songs, current index:', queueIndexRef.current);
    
    if (!musicKitRef.current) {
      console.log('[MusicKit] No MusicKit instance');
      return;
    }

    // When native queue is active (Party Mode / AutoMix), MusicKit handles transitions
    // Just update our internal index tracking from MusicKit's state
    if (nativeQueueActiveRef.current) {
      console.log('[MusicKit] Native queue active - letting MusicKit handle auto-advance');
      // MusicKit auto-advances; nowPlayingItemDidChange syncs our index
      // Only intervene if MusicKit reached the very end of its queue
      const mkQueue = musicKitRef.current.queue;
      const mkQueueLength = mkQueue?.items?.length || mkQueue?.length || 0;
      const mkQueuePosition = mkQueue?.position ?? mkQueue?.indexForItem?.(musicKitRef.current.nowPlayingItem) ?? -1;
      console.log('[MusicKit] MusicKit queue position:', mkQueuePosition, '/', mkQueueLength);
      
      // If MusicKit has more songs, do nothing - it will auto-advance
      if (mkQueueLength > 0 && mkQueuePosition < mkQueueLength - 1) {
        console.log('[MusicKit] MusicKit queue has more songs, skipping manual advance');
        return;
      }
      
      // MusicKit reached end of its queue - restart from beginning for continuous mix
      const currentQueue = queueRef.current;
      if (currentQueue.length > 1) {
        console.log('[MusicKit] Restarting queue from beginning for continuous mix');
        const songIds = currentQueue.map(q => q.id).filter(Boolean);
        try {
          await musicKitRef.current.setQueue({ songs: songIds });
          musicKitRef.current.shuffleMode = 0;
          musicKitRef.current.repeatMode = 0;
          await musicKitRef.current.play();
          queueIndexRef.current = 0;
          setQueueIndex(0);
        } catch (err) {
          console.error('[MusicKit] Queue restart error:', err);
        }
      }
      return;
    }

    // Repeat one - replay current song
    if (repeatMode === 2) {
      console.log('[MusicKit] Repeat one mode - replaying');
      try {
        await musicKitRef.current.seekToTime(0);
        await musicKitRef.current.play();
      } catch (err) {
        console.error('Repeat one error:', err);
      }
      return;
    }

    // CRITICAL: Use refs for queue data to get latest values
    const currentQueue = queueRef.current;
    const currentIndex = queueIndexRef.current;

    // Check if there's a next song in queue
    if (currentQueue.length > 0) {
      let nextIndex;
      
      if (shuffleMode) {
        // Pick random index (different from current if possible)
        if (currentQueue.length > 1) {
          do {
            nextIndex = Math.floor(Math.random() * currentQueue.length);
          } while (nextIndex === currentIndex);
        } else {
          nextIndex = 0;
        }
      } else {
        nextIndex = currentIndex + 1;
      }

      // Check if we've reached the end
      if (nextIndex >= currentQueue.length) {
        if (repeatMode === 1) { // Repeat all
          nextIndex = 0;
        } else {
          // No repeat - stop
          console.log('[MusicKit] End of queue, no repeat');
          return;
        }
      }

      console.log('[MusicKit] Playing next song at index:', nextIndex, 'of', currentQueue.length);
      
      // Update both ref and state
      queueIndexRef.current = nextIndex;
      setQueueIndex(nextIndex);
      
      const nextSong = currentQueue[nextIndex];
      console.log('[MusicKit] Next song:', nextSong);
      
      if (nextSong?.id) {
        try {
          console.log('[MusicKit] Setting queue with song ID:', nextSong.id);
          await musicKitRef.current.setQueue({ songs: [nextSong.id] });
          await musicKitRef.current.play();
          console.log('[MusicKit] Auto-play next song started successfully');
        } catch (err) {
          console.error('[MusicKit] Auto-play next error:', err);
        }
      } else {
        console.error('[MusicKit] Next song has no ID:', nextSong);
      }
    } else {
      console.log('[MusicKit] No songs in queue');
    }
  }, [repeatMode, shuffleMode]);
  
  // Keep the ref updated with the latest handleTrackEnded function
  useEffect(() => {
    handleTrackEndedRef.current = handleTrackEnded;
  }, [handleTrackEnded]);
  
  // Set external callback for track ended - used by SessionView
  const setOnTrackEndedCallback = useCallback((callback) => {
    console.log('[MusicKit] setOnTrackEndedCallback called with:', callback ? 'function' : 'null');
    onTrackEndedCallbackRef.current = callback;
    // When callback is cleared (null), increment version so PersistentMiniPlayer can re-register
    if (callback === null) {
      setCallbackVersion(prev => prev + 1);
    }
  }, []);

  // Toggle shuffle mode - controls actual MusicKit shuffle
  const toggleShuffle = useCallback(() => {
    if (musicKitRef.current) {
      const newMode = musicKitRef.current.shuffleMode === 0 ? 1 : 0;
      musicKitRef.current.shuffleMode = newMode;
      console.log('[MusicKit] Shuffle mode toggled:', newMode === 0 ? 'OFF' : 'ON');
    }
    setShuffleMode(prev => !prev);
  }, []);

  // Cycle through repeat modes: off -> all -> one -> off
  const toggleRepeat = useCallback(() => {
    setRepeatMode(prev => (prev + 1) % 3);
  }, []);

  // Set queue from session songs
  const setPlayQueue = useCallback((songs, startIndex = 0) => {
    setQueue(songs);
    setQueueIndex(startIndex);
  }, []);

  // Search for artists using Apple Music API
  const searchArtists = useCallback(async (query) => {
    if (!musicKitRef.current || !query || query.length < 2) {
      return [];
    }

    try {
      const response = await musicKitRef.current.api.music(
        `/v1/catalog/us/search?term=${encodeURIComponent(query)}&types=artists&limit=20`
      );
      
      const artistsData = response?.data?.results?.artists?.data || [];
      
      // Map artists with properly formatted artwork URLs
      return artistsData.map(artist => ({
        id: artist.id,
        name: artist.attributes?.name || 'Unknown Artist',
        // CRITICAL: Replace {w} and {h} placeholders with actual pixel values
        image_url: artist.attributes?.artwork?.url
          ? artist.attributes.artwork.url
              .replace('{w}', '300')
              .replace('{h}', '300')
          : null,
        genre: artist.attributes?.genreNames?.[0] || 'Music'
      }));
    } catch (error) {
      console.error('Artist search failed:', error);
      return [];
    }
  }, []);

  // Search for songs using Apple Music API
  const searchSongs = useCallback(async (query) => {
    if (!musicKitRef.current || !query || query.length < 2) {
      return [];
    }

    try {
      const response = await musicKitRef.current.api.music(
        `/v1/catalog/us/search?term=${encodeURIComponent(query)}&types=songs&limit=20`
      );
      
      const songsData = response?.data?.results?.songs?.data || [];
      return songsData;
    } catch (error) {
      console.error('Song search failed:', error);
      return [];
    }
  }, []);

  // Get current queue position
  const getQueuePosition = useCallback(() => {
    return { current: queueIndex, total: queue.length };
  }, [queueIndex, queue.length]);

  const value = {
    // State
    musicKit,
    isConfigured,
    isAuthorized,
    isPlaying,
    currentSong,
    playbackTime,
    duration,
    volume,
    error,
    clearError: () => setError(null),
    isLoading,
    
    // Music source tracking: 'playlist', 'dm', 'session', or null
    musicSource,
    setMusicSource,
    
    // Shuffle and Repeat state
    shuffleMode,
    repeatMode, // 0: off, 1: repeat all, 2: repeat one
    
    // Queue state
    queue,
    queueIndex,
    
    // Actions
    authorize,
    unauthorize,
    playSong,
    playSongWithUpcoming,
    loadSongAtPosition, // Load song at specific position without starting from beginning
    playQueue,
    play,
    pause,
    stop,
    togglePlayPause,
    skipToNext,
    skipToPrevious,
    getPreviewUrl,
    seekTo,
    setVolume: setPlayerVolume,
    
    // Search
    searchArtists,
    searchSongs,
    
    // Shuffle and Repeat controls
    toggleShuffle,
    toggleRepeat,
    
    // Queue management
    setPlayQueue,
    getQueuePosition,
    
    // External callbacks
    setOnTrackEndedCallback,
    callbackVersion, // Used by PersistentMiniPlayer to detect when to re-register callback
    
    // Apple Music duplicate detection (STRICT POLICY: 1 Apple Music = 1 Profile)
    appleMusicError,
    linkedUsername,
    clearAppleMusicError,
  };

  return (
    <MusicKitContext.Provider value={value}>
      {children}
    </MusicKitContext.Provider>
  );
};

export default MusicKitContext;
